import React, { FC, useState, useEffect } from 'react';
import { Core } from '@everfi/reactifi';
import { ResultsCount, UnAuthenticatedWrapper, BackButton } from '../Shared';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHeapTracker } from '@lib/useHeapTracker';
import { useTranslation } from 'react-i18next';
import { fetchData } from '../../mocks/scripts/utils';

export const Search: FC = () => {
  useHeapTracker();
  const { t } = useTranslation();
  const pageSize = 10;
  const [activePage, setActivePage] = useState(1);
  const [searchData, setSearchData] = useState([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredData, setFilteredData] = useState([]);

  const totalCount = filteredData.length;

  const startIndex = (activePage - 1) * pageSize;
  const currentItems = filteredData.slice(startIndex, startIndex + pageSize);

  const handlePageChange = (newPage: number) => {
    setActivePage(newPage);
  };

  useEffect(() => {
    const loadData = async () => {
      const result = await fetchData();
      result.forEach((res: any) => {
        res.children = <Core.Tag colorScheme={'gray'}>Type</Core.Tag>;
      });
      setSearchData(result);
      setFilteredData(result);
    };

    loadData();
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const term = params.get('keyword') || '';
    setSearchTerm(term);
  }, [location.search]);

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);

    navigate({ search: `?keyword=${newSearchTerm}` });
  };
  useEffect(() => {
    const filtered = searchData.filter(
      (item: Core.ContentListItem) =>
        item.heading?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
    setActivePage(1);
  }, [searchTerm, searchData]);

  const header = <BackButton path={`:slug/learner/:program_slug`} />;

  return (
    <UnAuthenticatedWrapper header={header}>
      <Core.Layout variant={'flex'} maxW={'container.lg'} mx={'auto'} flexDirection={'column'} gap={'sm'}>
        <Core.Layout variant={'flex'} flexDirection={'row'} justifyContent={'space-between'} mt={'sm'}>
          <Core.Layout>
            <Core.Typography textStyle={'h4'}>{t('Search')}</Core.Typography>
            <Core.Typography>{t('Explore or search the content library to find what matters to you')}</Core.Typography>
          </Core.Layout>
          <Core.Layout maxW={'sm'}>
            <Core.Input type={Core.FieldTypes.search} onChange={handleSearchTermChange} value={searchTerm}></Core.Input>
          </Core.Layout>
        </Core.Layout>

        <ResultsCount activePage={activePage} pageSize={pageSize} totalCount={totalCount} />
        <Core.Layout bg={'surface-page'} mb={'sm'}>
          <Core.Layout w={'container.lg'} mx={'auto'}>
            {totalCount > 0 ? (
              <>
                <Core.ContentList items={currentItems} variant="withCircularProgress" textStyle="h4" />
                <Core.Layout mb="8px" variant="flex" justifyContent="center">
                  <Core.Pagination
                    activePage={activePage}
                    data={filteredData}
                    pageSize={pageSize}
                    totalCount={totalCount}
                    isLoadingData={true}
                    maxButtons={5}
                    onPageChange={handlePageChange}
                  />
                </Core.Layout>
              </>
            ) : (
              <Core.Layout variant={'flex'} alignItems={'center'} flexDirection={'column'} p={'sm'}>
                <Core.Layout variant={'flex'} alignItems={'center'} flexDirection={'column'} gap={'sm'}>
                  <Core.Icon iconName={'search'} />
                  <Core.Typography textStyle={'h5'}>{t('No results found')}</Core.Typography>
                </Core.Layout>
                <Core.Typography textStyle={'p3'}>{t('Try searching for another term')}</Core.Typography>
              </Core.Layout>
            )}
          </Core.Layout>
        </Core.Layout>
      </Core.Layout>
    </UnAuthenticatedWrapper>
  );
};
